"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";

// DAR GraphQL takes ~300-500ms per request; a 3-char min + 400ms debounce keeps
// call volume down without the customer noticing added latency.
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 400;

export function useAddressSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const posthog = usePostHog();

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await fetch(`/api/address/search?q=${encodeURIComponent(trimmed)}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          setResults([]);
          setError("Søgning fejlede");
          posthog?.capture("address_search", { query: trimmed, has_result: false });
          return;
        }
        const data: AddressSuggestion[] = await res.json();
        setResults(data);
        posthog?.capture("address_search", {
          query: trimmed,
          has_result: data.length > 0,
          result_count: data.length,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setResults([]);
        setError("Søgning fejlede");
        posthog?.capture("address_search", { query: trimmed, has_result: false });
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, posthog]);

  return { query, setQuery, results, isLoading, error };
}
